import { faEdit } from '@fortawesome/free-regular-svg-icons'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Authorization from '../../Authentication/Authorization'
import DialogContext from '../../Authentication/DialogContext'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FormDataControl } from '../../components/FormFields'
import Loading from '../../layout/Loading'
import MasterKeyService from '../../service/MasterKeyService'
import GatewayType from '../GatewayType'

const MasterKey: GatewayType = () => {
  const { t } = useTranslation()
  const [masterkeyDetails, setMasterkeyDetails] = useState({} as any)
  const [loading, setLoading] = useState(true)
  const { editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    MasterKeyService.get(setMasterkeyDetails).finally(() => setLoading(false))
  }, [])
  const columns = useMemo(() => {
    return [
      {
        text: 'label',
        dataField: 'label',
        classes: 'font-weight-bolder',
        sort: true,
      },
      {
        text: 'value',
        dataField: 'value',
      },
    ]
  }, [])

  const data = getData(masterkeyDetails, t)

  return (
    <Loading isLoading={loading}>
      <DataTable
        keyField='label'
        data={data}
        columns={columns}
        headerClasses='d-none'
        searchable={false}
        caption={t('masterkey.header')}
        captionButtons={[
          {
            onClick: () => changeMasterkeyDialog(t, editDialogPropsSetter, masterkeyDetails, setMasterkeyDetails),
            text: t('masterkey.changeHeader'),
            icon: faEdit,
            permissions: Authorization.settings.masterKey,
          },
        ]}
      />
    </Loading>
  )
}

function getData(config: any, t: TFunction) {
  var list: any = []
  if (Object.keys(config).length > 0) {
    list.push({
      label: t('masterkey.methodName'),
      value: config.methodName,
    })
    if (config.options) {
      for (const [key, value] of Object.entries(config.options)) {
        list.push({
          label: t(`masterkey.${key}`),
          value: value,
        })
      }
    }
  }
  return list
}

const hsmKeyTypes = [
  { label: 'LUNA HSM', value: 'LUNA' },
  { label: 'UTIMACO HSM', value: 'UTIMACO' },
]

function getFinalField(t: TFunction, custom: string[], masterkeyDetails: any): FormDataControl[] {
  const method = custom[0].toString()
  if (method === 'RAM') {
    return [
      {
        id: 'ramPassword',
        type: 'password',
        label: t('masterkey.ramPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.ramPassword') }),
      },
      {
        id: 'confirmPassword',
        type: 'password',
        label: t('masterkey.repeatPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.ramPassword') }),
      },
    ]
  } else if (method === 'HSMEPERI') {
    return [
      {
        id: 'hsmType',
        type: 'select',
        options: [
          { label: 'Select a HSM type', value: '' },
          ...hsmKeyTypes.map((hsmKey) => {
            return { label: hsmKey.label, value: hsmKey.value }
          }),
        ],
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], [e.target.value].toString()]),
        value: custom[1],
        label: 'HSM Type',
        required: true,
        requiredMessage: t('invalidMessage', { label: 'HSM Type' }),
      },
      ...getFinalEperiHSMFields(t, custom, masterkeyDetails),
    ]
  } else if (method === 'HSMRAM') {
    return [
      {
        id: 'hsmType',
        type: 'select',
        options: [
          { label: 'Select a HSM Type', value: '' },
          ...hsmKeyTypes.map((hsmKey) => {
            return { label: hsmKey.label, value: hsmKey.value }
          }),
        ],
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], [e.target.value].toString()]),
        value: custom[1],
        label: 'HSM Type',
        required: true,
        requiredMessage: t('invalidMessage', { label: 'HSM Type' }),
      },
      ...getFinalHSMRAMField(t, custom, masterkeyDetails),
    ]
  }
  return []
}

function getFinalHSMRAMField(t: TFunction, custom: string[], masterkeyDetails: any): FormDataControl[] {
  const type = typeof custom[1] !== 'undefined' ? custom[1].toString() : 'undefined'
  if (type === 'LUNA') {
    return [
      {
        id: 'hsmPartition',
        type: 'text',
        label: t('masterkey.hsmPartition'),
        value: masterkeyDetails?.options?.hsmPartition,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmPartition') }),
      },
      {
        id: 'ramPassword',
        type: 'password',
        label: t('masterkey.ramPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.ramPassword') }),
      },
      {
        id: 'confirmPassword',
        type: 'password',
        label: t('masterkey.repeatPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.ramPassword') }),
      },
      {
        id: 'hsmPassword',
        type: 'password',
        label: t('masterkey.hsmPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmPassword') }),
      },
      {
        id: 'hsmKeyAlias',
        type: 'text',
        label: t('masterkey.keyAlias'),
        value: masterkeyDetails?.options?.hsmKeyAlias,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.keyAlias') }),
      },
    ]
  } else if (type === 'UTIMACO') {
    return [
      {
        id: 'hsmDevice',
        type: 'text',
        label: t('masterkey.hsmDevice'),
        value: masterkeyDetails?.options?.hsmDevice,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmDevice') }),
      },
      {
        id: 'ramPassword',
        type: 'password',
        label: t('masterkey.ramPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.ramPassword') }),
      },
      {
        id: 'confirmPassword',
        type: 'password',
        label: t('masterkey.repeatPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.ramPassword') }),
      },
      {
        id: 'hsmUsername',
        type: 'text',
        label: t('masterkey.hsmUserName'),
        value: masterkeyDetails?.options?.hsmUserName,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmUserName') }),
      },
      {
        id: 'hsmPassword',
        type: 'password',
        label: t('masterkey.hsmPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmPassword') }),
      },
      {
        id: 'hsmKeyAlias',
        type: 'text',
        label: t('masterkey.keyAlias'),
        value: masterkeyDetails?.options?.hsmKeyAlias,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.keyAlias') }),
      },
      {
        id: 'hsmKeyGroup',
        type: 'text',
        label: t('masterkey.hsmKeyGroup'),
        value: masterkeyDetails?.options?.hsmKeyGroup,
      },
      {
        id: 'hsmKeySpecifier',
        type: 'text',
        label: t('masterkey.hsmKeySpecifier'),
        value: masterkeyDetails?.options?.hsmKeySpecifier,
      },
    ]
  }

  return []
}

function getFinalEperiHSMFields(t: TFunction, custom: string[], masterkeyDetails: any): FormDataControl[] {
  const type = typeof custom[1] !== 'undefined' ? custom[1].toString() : 'undefined'
  if (type === 'LUNA') {
    return [
      {
        id: 'hsmPartition',
        type: 'text',
        label: t('masterkey.hsmPartition'),
        value: masterkeyDetails?.options?.hsmPartition,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmPartition') }),
      },
      {
        id: 'hsmPassword',
        type: 'password',
        label: t('masterkey.hsmPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmPassword') }),
      },
      {
        id: 'hsmKeyAlias',
        type: 'text',
        label: t('masterkey.keyAlias'),
        value: masterkeyDetails?.options?.hsmKeyAlias,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.keyAlias') }),
      },
    ]
  } else if (type === 'UTIMACO') {
    return [
      {
        id: 'hsmDevice',
        type: 'text',
        label: t('masterkey.hsmDevice'),
        value: masterkeyDetails?.options?.hsmDevice,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmDevice') }),
      },
      {
        id: 'hsmUsername',
        type: 'text',
        label: t('masterkey.hsmUserName'),
        value: masterkeyDetails?.options?.hsmUsername,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmUserName') }),
      },
      {
        id: 'hsmPassword',
        type: 'password',
        label: t('masterkey.hsmPassword'),
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.hsmPassword') }),
      },
      {
        id: 'hsmKeyAlias',
        type: 'text',
        label: t('masterkey.keyAlias'),
        value: masterkeyDetails?.options?.hsmKeyAlias,
        required: true,
        requiredMessage: t('invalidMessage', { label: t('masterkey.keyAlias') }),
      },
      {
        id: 'hsmKeyGroup',
        type: 'text',
        label: t('masterkey.hsmKeyGroup'),
        value: masterkeyDetails?.options?.hsmKeyGroup,
      },
      {
        id: 'hsmKeySpecifier',
        type: 'text',
        label: t('masterkey.hsmKeySpecifier'),
        value: masterkeyDetails?.options?.hsmKeySpecifier,
      },
    ]
  }
  return []
}

function changeMasterkeyDialog(t: TFunction, editDialogPropsSetter: any, masterkeyDetails: any, setMasterkeyDetails: React.Dispatch<any[]>) {
  const availableMethods = [
    { label: 'Clear Text', value: 'CLEAR_TEXT' },
    { label: 'Eperi Encryption', value: 'EPERI' },
    { label: 'RAM Password', value: 'RAM' },
    { label: 'HSM Eperi Encryption', value: 'HSMEPERI' },
    { label: 'HSM RAM Password', value: 'HSMRAM' },
  ]

  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('masterkey.changeHeader'),
    onSubmit: MasterKeyService.update,
    onSubmitExtraArgs: [t, setMasterkeyDetails],
    data: (custom: string[]) => [
      {
        id: 'methodName',
        type: 'select',
        options: [
          ...availableMethods.map((method) => {
            return { label: method.label, value: method.value }
          }),
        ],
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([[e.target.value].toString(), custom[1]]),
        value: custom[0],
        label: 'Method Name',
        required: true,
        requiredMessage: t('invalidMessage', { label: 'Method Name' }),
      },
      ...getFinalField(t, custom, masterkeyDetails),
    ],
    initCustom: ['HSMEPERI', 'HSMRAM'].includes(masterkeyDetails.methodName)
      ? [masterkeyDetails.methodName, masterkeyDetails.options.hsmType]
      : [masterkeyDetails.methodName],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

export default MasterKey
